// Topbar.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import { BottomSheet } from 'react-spring-bottom-sheet';

import appSettings from '../../../config/settings';
import { useConfiguration } from '../../../context/configurationContext';
import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../../util/search';
import { parse, stringify } from '../../../util/urlHelpers';
import { createResourceLocatorString, matchPathname, pathByRouteName } from '../../../util/routes';
import {
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  RenderEmailVerification,
} from '../../../components';

import MenuIcon from './MenuIcon';
import NotificationBellIcon from './NotificationBellIcon';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';
import NotificationsPanel from './NotificationsPanel/NotificationsPanel';
import AuthNavLink from './AuthNavLink';
import GuestLanguageSelector from './GuestLanguageSelector';

import css from './Topbar.module.css';
import {
  fetchNotificationsState,
  setNotificationsUnreadCount,
} from '../../../ducks/notifications.duck';
import { clearStreamChatNotificationsApiCache } from '../../../util/api';
import { clearNotificationsFeedCache } from '../../../util/notificationsFeed';
import { getActiveRoleUpdatePayload } from '../../../util/activeRole';
import { updateProfile } from '../../ProfileSettingsPage/ProfileSettingsPage.duck';

const MAX_MOBILE_SCREEN_WIDTH = 1024;
const NOTIFICATIONS_SYNC_EVENT = 'notifications_updated';
const NOTIFICATIONS_SYNC_CHANNEL = 'sceneaccess_notifications';
const FORCED_STREAM_REFRESH_THROTTLE_MS = 2500;

const parseNotificationsSyncPayload = value => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    if (value === NOTIFICATIONS_SYNC_EVENT) {
      return {
        type: NOTIFICATIONS_SYNC_EVENT,
        forceNotificationsFresh: false,
        forceStreamFresh: false,
      };
    }

    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return {
        type: NOTIFICATIONS_SYNC_EVENT,
        forceNotificationsFresh: false,
        forceStreamFresh: false,
      };
    }
  }

  return value && typeof value === 'object' ? value : null;
};

const SEARCH_DISPLAY_ALWAYS = 'always';
const SEARCH_DISPLAY_NOT_LANDING_PAGE = 'notLandingPage';
const SEARCH_DISPLAY_ONLY_SEARCH_PAGE = 'onlySearchPage';

const redirectToURLWithModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (history, location, modalStateParam) => {
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => k !== modalStateParam);
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const isPrimary = o => o.group === 'primary';
const isSecondary = o => o.group === 'secondary';
const compareGroups = (a, b) => {
  const isAHigherGroupThanB = isPrimary(a) && isSecondary(b);
  const isALesserGroupThanB = isSecondary(a) && isPrimary(b);
  return isAHigherGroupThanB ? -1 : isALesserGroupThanB ? 1 : 0;
};

const sortCustomLinks = customLinks => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.sort(compareGroups);
};

const getResolvedCustomLinks = (customLinks, routeConfiguration) => {
  const links = Array.isArray(customLinks) ? customLinks : [];
  return links.map(linkConfig => {
    const { type, href } = linkConfig;
    const isInternalLink = type === 'internal' || href.charAt(0) === '/';
    if (isInternalLink) {
      try {
        const testURL = new URL('http://my.marketplace.com' + href);
        const matchedRoutes = matchPathname(testURL.pathname, routeConfiguration);
        if (matchedRoutes.length > 0) {
          const found = matchedRoutes[0];
          const to = { search: testURL.search, hash: testURL.hash };
          return {
            ...linkConfig,
            route: {
              name: found.route?.name,
              params: found.params,
              to,
            },
          };
        }
      } catch (e) {
        return linkConfig;
      }
    }
    return linkConfig;
  });
};

const isCMSPage = found =>
  found.route?.name === 'CMSPage' ? `CMSPage:${found.params?.pageId}` : null;
const isInboxPage = found =>
  found.route?.name === 'InboxPage' ? `InboxPage:${found.params?.tab}` : null;

const getResolvedCurrentPage = (location, routeConfiguration) => {
  const matchedRoutes = matchPathname(location.pathname, routeConfiguration);
  if (matchedRoutes.length > 0) {
    const found = matchedRoutes[0];
    const cmsPageName = isCMSPage(found);
    const inboxPageName = isInboxPage(found);
    return cmsPageName ? cmsPageName : inboxPageName ? inboxPageName : `${found.route?.name}`;
  }
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

export const TopbarComponent = props => {
  const dispatch = useDispatch();

  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    authStateReady,
    isAuthenticated,
    isLoggedInAs,
    authScopes = [],
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentUserAttentionTransactions = [],
    currentPage,
    notificationCount = 0,
    intl,
    history,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    routeConfiguration,
    showTopbarSearch = false,
  } = props;

  const [isDesktopNotificationsOpen, setIsDesktopNotificationsOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const currentUserId = currentUser?.id?.uuid || null;
  const forcedRefreshRef = useRef({
    lastRunAt: 0,
    timeoutId: null,
    pendingOptions: null,
  });

  const handleNotificationRoleSwitch = useCallback(
    targetRole => {
      if (!currentUser?.id) {
        return Promise.reject(new Error('Cannot switch notification role without a current user'));
      }

      return Promise.resolve(
        dispatch(updateProfile(getActiveRoleUpdatePayload(currentUser, targetRole)))
      ).then(updatedUser => {
        if (!updatedUser?.id) {
          throw new Error('Failed to persist notification target role');
        }

        return updatedUser;
      });
    },
    [currentUser, dispatch]
  );

  const refreshNotificationsState = useCallback(
    (options = {}) => {
      const request = dispatch(fetchNotificationsState(options));
      return request && typeof request.catch === 'function'
        ? request.catch(() => null)
        : Promise.resolve(request);
    },
    [dispatch]
  );

  const scheduleNotificationsStateRefresh = useCallback(
    (options = {}) => {
      const {
        forceNotificationsFresh = false,
        forceStreamFresh = false,
        reason = 'sync-event',
      } = options;

      if ((!forceNotificationsFresh && !forceStreamFresh) || typeof window === 'undefined') {
        return refreshNotificationsState(options);
      }

      const state = forcedRefreshRef.current;
      const now = Date.now();
      const elapsedMs = now - state.lastRunAt;

      if (elapsedMs >= FORCED_STREAM_REFRESH_THROTTLE_MS) {
        if (state.timeoutId) {
          window.clearTimeout(state.timeoutId);
          state.timeoutId = null;
        }

        state.pendingOptions = null;
        state.lastRunAt = now;
        return refreshNotificationsState({
          forceNotificationsFresh,
          forceStreamFresh,
          reason,
        });
      }

      state.pendingOptions = {
        reason,
        forceNotificationsFresh:
          !!forceNotificationsFresh || !!state.pendingOptions?.forceNotificationsFresh,
        forceStreamFresh: !!forceStreamFresh || !!state.pendingOptions?.forceStreamFresh,
      };

      if (!state.timeoutId) {
        state.timeoutId = window.setTimeout(() => {
          const nextOptions = state.pendingOptions || {
            reason,
            forceNotificationsFresh,
            forceStreamFresh,
          };

          state.timeoutId = null;
          state.pendingOptions = null;
          state.lastRunAt = Date.now();
          refreshNotificationsState(nextOptions);
        }, FORCED_STREAM_REFRESH_THROTTLE_MS - elapsedMs);
      }

      return Promise.resolve(null);
    },
    [refreshNotificationsState]
  );

  const handleUnreadCountChange = useCallback(
    unreadCount => {
      dispatch(setNotificationsUnreadCount(unreadCount));
    },
    [dispatch]
  );

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    setIsDesktopNotificationsOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    return () => {
      const { timeoutId } = forcedRefreshRef.current;
      if (timeoutId && typeof window !== 'undefined') {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      clearNotificationsFeedCache();
      clearStreamChatNotificationsApiCache();
      dispatch(setNotificationsUnreadCount(0));
      return undefined;
    }

    refreshNotificationsState({
      reason: 'initial-load',
      forceNotificationsFresh: true,
      forceStreamFresh: true,
    });

    const intervalId = window.setInterval(() => {
      // Focus/visibility handlers below refresh immediately when returning.
      if (document.visibilityState === 'hidden') return;
      refreshNotificationsState({ reason: 'interval' });
    }, 60000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch, isAuthenticated, currentUserId, refreshNotificationsState]);

  useEffect(() => {
    if (!isAuthenticated || typeof window === 'undefined') {
      return undefined;
    }

    const handleFocusedRefresh = () => {
      scheduleNotificationsStateRefresh({
        forceNotificationsFresh: true,
        forceStreamFresh: true,
        reason: 'window-focus',
      });
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        handleFocusedRefresh();
      }
    };

    window.addEventListener('focus', handleFocusedRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocusedRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, scheduleNotificationsStateRefresh]);

  useEffect(() => {
    if (!isAuthenticated || typeof window === 'undefined') {
      return undefined;
    }

    let channel;
    const handleStorage = event => {
      if (event?.key === NOTIFICATIONS_SYNC_EVENT) {
        const payload = parseNotificationsSyncPayload(event?.newValue);
        scheduleNotificationsStateRefresh({
          forceNotificationsFresh: !!payload?.forceNotificationsFresh,
          forceStreamFresh: !!payload?.forceStreamFresh,
          reason: 'sync-event',
        });
      }
    };

    const handleBroadcast = event => {
      const payload = parseNotificationsSyncPayload(event?.data);
      if (payload?.type === NOTIFICATIONS_SYNC_EVENT) {
        scheduleNotificationsStateRefresh({
          forceNotificationsFresh: !!payload?.forceNotificationsFresh,
          forceStreamFresh: !!payload?.forceStreamFresh,
          reason: 'sync-event',
        });
      }
    };

    if ('BroadcastChannel' in window) {
      channel = new BroadcastChannel(NOTIFICATIONS_SYNC_CHANNEL);
      channel.addEventListener('message', handleBroadcast);
    }

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
      if (channel) {
        channel.removeEventListener('message', handleBroadcast);
        channel.close();
      }
    };
  }, [isAuthenticated, refreshNotificationsState, scheduleNotificationsStateRefresh]);

  const handleSubmit = values => {
    const { currentSearchParams, history, config, routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };

    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };

  const handleLogout = () => {
    const { onLogout, history, routeConfiguration } = props;
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration);

      if (appSettings.dev) {
        history.push(path);
      } else if (typeof window !== 'undefined') {
        window.location = path;
      }

      // eslint-disable-next-line no-console
      console.log('logged out');
    });
  };

  const { mobilemenu, mobilenotifications, keywords, address, origin, bounds } = parse(
    location.search,
    {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    }
  );

  const sortedCustomLinks = sortCustomLinks(config.topbar?.customLinks);
  const customLinks = getResolvedCustomLinks(sortedCustomLinks, routeConfiguration);
  const resolvedCurrentPage = currentPage || getResolvedCurrentPage(location, routeConfiguration);

  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout = hasMatchMedia
    ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
    : true;

  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileNotificationsOpen = isMobileLayout && mobilenotifications === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      intl={intl}
      authStateReady={authStateReady}
      isAuthenticated={isAuthenticated}
      currentUserHasListings={currentUserHasListings}
      currentUser={currentUser}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={resolvedCurrentPage}
      customLinks={customLinks}
      onManageDisableScrolling={onManageDisableScrolling}
    />
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;

    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };

  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className);

  const { display: searchFormDisplay = SEARCH_DISPLAY_ALWAYS } = config?.topbar?.searchBar || {};

  const showSearchOnAllPages = searchFormDisplay === SEARCH_DISPLAY_ALWAYS;
  const showSearchOnSearchPage =
    searchFormDisplay === SEARCH_DISPLAY_ONLY_SEARCH_PAGE && resolvedCurrentPage === 'SearchPage';
  const showSearchNotOnLandingPage =
    searchFormDisplay === SEARCH_DISPLAY_NOT_LANDING_PAGE && resolvedCurrentPage !== 'LandingPage';
  const showSearchOnLandingPageAfterHero =
    resolvedCurrentPage === 'LandingPage' && showTopbarSearch;

  const showSearchForm =
    showSearchOnAllPages ||
    showSearchOnSearchPage ||
    showSearchNotOnLandingPage ||
    showSearchOnLandingPageAfterHero;

  const notificationBadgeText = notificationCount > 99 ? '99+' : `${notificationCount}`;
  const notificationsLabel = intl.formatMessage({
    id: 'Notifications.title',
    defaultMessage: 'Notifications',
  });

  const mobileNotificationsButtonMaybe =
    mounted && authStateReady && isAuthenticated ? (
      <Button
        rootClassName={css.notificationMenu}
        onClick={() => redirectToURLWithModalState(history, location, 'mobilenotifications')}
        title={notificationsLabel}
        data-testid="notifications-bell-mobile"
      >
        <NotificationBellIcon className={css.notificationBellIcon} />
        {notificationCount > 0 ? (
          <span className={css.notificationBadge}>{notificationBadgeText}</span>
        ) : null}
      </Button>
    ) : (
      <div className={css.notificationMenuPlaceholder} aria-hidden="true" />
    );

  const showMobileGuestActions = mounted && authStateReady && !isAuthenticated;
  const mobileGuestActionsMaybe = showMobileGuestActions ? (
    <div className={css.mobileGuestActions}>
      <AuthNavLink name="SignupPage" className={css.mobileGuestAuthLink}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </AuthNavLink>
      <AuthNavLink name="LoginPage" className={css.mobileGuestAuthLink}>
        <FormattedMessage id="TopbarDesktop.login" />
      </AuthNavLink>
      <GuestLanguageSelector
        intl={intl}
        onManageDisableScrolling={onManageDisableScrolling}
        buttonClassName={css.mobileLanguageButton}
        dataTestId="mobile-header-language-menu-trigger"
      />
    </div>
  ) : null;

  const desktopNotificationsPanel = (
    <NotificationsPanel
      variant="desktop"
      isOpen={isDesktopNotificationsOpen}
      history={history}
      currentUser={currentUser}
      legacyAttentionTransactions={currentUserAttentionTransactions}
      unreadCount={notificationCount}
      onClose={() => setIsDesktopNotificationsOpen(false)}
      onUnreadCountChange={handleUnreadCountChange}
      onRoleSwitch={handleNotificationRoleSwitch}
    />
  );

  const mobileNotificationsPanel = (
    <NotificationsPanel
      variant="mobile"
      isOpen={isMobileNotificationsOpen}
      history={history}
      currentUser={currentUser}
      legacyAttentionTransactions={currentUserAttentionTransactions}
      unreadCount={notificationCount}
      onClose={() => redirectToURLWithoutModalState(history, location, 'mobilenotifications')}
      onUnreadCountChange={handleUnreadCountChange}
      onRoleSwitch={handleNotificationRoleSwitch}
      showDragHandle={false}
    />
  );

  return (
    <div className={classes}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        isLoggedInAs={isLoggedInAs}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={resolvedCurrentPage}
      />

      <div
        className={classNames(
          mobileRootClassName || css.container,
          { [css.guestMobileContainer]: showMobileGuestActions },
          mobileClassName
        )}
      >
        <Button
          rootClassName={classNames(css.menu, { [css.guestMobileMenu]: showMobileGuestActions })}
          onClick={() => redirectToURLWithModalState(history, location, 'mobilemenu')}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon className={css.menuIcon} />
        </Button>

        <LinkedLogo
          className={classNames({ [css.guestMobileLogoLink]: showMobileGuestActions })}
          logoImageClassName={classNames({ [css.guestMobileLogoImage]: showMobileGuestActions })}
          layout={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          linkToExternalSite={config?.topbar?.logoLink}
        />

        {mobileGuestActionsMaybe || mobileNotificationsButtonMaybe}
      </div>

      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={resolvedCurrentPage}
          authStateReady={authStateReady}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          onSearchSubmit={handleSubmit}
          config={config}
          customLinks={customLinks}
          showSearchForm={showSearchForm}
          showTopbarSearch={showTopbarSearch}
          isNotificationsOpen={isDesktopNotificationsOpen}
          onToggleNotifications={() => setIsDesktopNotificationsOpen(prev => !prev)}
          onCloseNotifications={() => setIsDesktopNotificationsOpen(false)}
          notificationsPanel={desktopNotificationsPanel}
          onManageDisableScrolling={onManageDisableScrolling}
        />
      </div>

      <Modal
        id="TopbarMobileMenu"
        className={css.mobileMenuModal}
        scrollLayerClassName={css.mobileMenuScrollLayer}
        containerClassName={css.mobileMenuContainer}
        closeButtonClassName={css.mobileMenuClose}
        isOpen={isMobileMenuOpen}
        onClose={() => redirectToURLWithoutModalState(history, location, 'mobilemenu')}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>

      <BottomSheet
        open={isMobileNotificationsOpen}
        onDismiss={() => redirectToURLWithoutModalState(history, location, 'mobilenotifications')}
        className={css.notificationsBottomSheet}
        defaultSnap={({ maxHeight }) => Math.min(maxHeight * 0.88, 720)}
        snapPoints={({ maxHeight }) => [Math.min(maxHeight * 0.88, 720)]}
        blocking
      >
        {mobileNotificationsPanel}
      </BottomSheet>

      <GenericError show={showGenericError} />
    </div>
  );
};

const Topbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();

  return (
    <>
      <TopbarComponent
        config={config}
        routeConfiguration={routeConfiguration}
        intl={intl}
        {...props}
      />
      <RenderEmailVerification />
    </>
  );
};

export default Topbar;
